export type NpcDifficulty = 'rookie' | 'sport' | 'pro';
/** Private rooms need the invite link; public rooms also appear in the lobby browser. */
export type RoomVisibility = 'private' | 'public';
export type RacePhase = 'lobby' | 'countdown' | 'racing' | 'finished';
export type RaceEventMode = 'circuit' | 'sprint' | 'time-trial' | 'style-rush' | 'elimination' | 'heat-run' | 'road-rage';
export type ArcadePreset = 'standard' | 'nitro-rush' | 'heavy-metal' | 'traffic-panic' | 'low-gravity';
export type CrewModule = 'balanced' | 'grip' | 'nitro' | 'armour';
export type SpoilerStyle = 'none' | 'ducktail' | 'gt';
export type WheelStyle = 'turbine' | 'mesh' | 'five-spoke';
export type DecalStyle = 'none' | 'center-stripe' | 'twin-stripe' | 'chevron' | 'circuit';
export type RivalStyle = 'grip' | 'drift' | 'bully' | 'sprinter' | 'trickster';
export type BossPhase = 'intro' | 'hunt' | 'pressure' | 'final';
export type RaceWagerKind = 'clean-finish' | 'clean-passes' | 'extreme-route';
export type RaceWagerStatus = 'inactive' | 'active' | 'complete' | 'failed';
export type PostRaceVote = 'rematch' | 'next-map' | 'surprise';
export type CitySetpieceKind = 'bern-tram' | 'luzern-drawbridge' | 'hannover-roadworks' | 'berlin-convoy';
export type StyleAction = '' | 'GOOD LAUNCH' | 'PERFECT LAUNCH' | 'WHEELSPIN' | 'DRIFT' | 'DRIFT BOOST' | 'DRAFT' | 'SLINGSHOT' | 'COMEBACK' | 'AIR' | 'NEAR MISS' | 'OVERTAKE' | 'CLEAN PASS' | 'REVENGE' | 'RISK LINE' | 'SHORTCUT' | 'LOOP MASTER' | 'GOOD CORNER' | 'PERFECT CORNER' | 'TAKEDOWN' | 'BOOST ZONE' | 'PIT CREW' | 'CROWD LINE' | 'LIVE CONTRACT' | 'HEAT UP' | 'BANKED' | 'SIGNATURE' | 'REWIND' | 'BOSS BLOCK' | 'BOSS ATTACK' | 'BOSS DRIFT' | 'BOSS SHORTCUT' | 'CHAIN LOST';
export type LiveChallengeKind = 'clean-sector' | 'pass-rival' | 'route-choice' | 'speed-trap' | 'style-chain';
export type LiveChallengeStatus = 'inactive' | 'active' | 'complete' | 'failed';
export type DynamicTrackEventKind = 'lane-closure' | 'street-crossing' | 'roadblock';
export type DynamicTrackEventPhase = 'warning' | 'active' | 'clearing';

export interface TrackPoint {
  x: number;
  z: number;
  y?: number;
  width?: number;
  /** Crossfall in degrees. Positive values raise the right-hand road edge. */
  bank?: number;
}

export interface MapPalette {
  skyTop: string;
  skyHorizon: string;
  fog: string;
  ground: string;
  road: string;
  shoulder: string;
  lane: string;
  accent: string;
}

export interface MapScenery {
  trees: number;
  rocks: number;
  buildings: number;
  lamps: boolean;
  barriers: boolean;
}

export interface RampDefinition {
  trackIndex: number;
  length: number;
  width: number;
  height: number;
}

export interface TunnelDefinition {
  trackIndex: number;
  length: number;
  width: number;
  height: number;
}

export interface RiskGateDefinition {
  trackIndex: number;
  side: 'left' | 'right';
  offset: number;
  width: number;
  reward: number;
}

export type TimeOfDay = 'dawn' | 'day' | 'sunset' | 'night';
export const WEATHER_VALUES = ['clear', 'haze', 'rain', 'sand', 'snow'] as const;
export type Weather = (typeof WEATHER_VALUES)[number];
const WEATHER_VALUE_SET: ReadonlySet<string> = new Set(WEATHER_VALUES);

export function isWeather(value: unknown): value is Weather {
  return typeof value === 'string' && WEATHER_VALUE_SET.has(value);
}

export interface AlternateRouteDefinition {
  id: string;
  name: string;
  kind: 'shortcut' | 'loop';
  /** Surface is both a visual promise and a small handling trade-off. */
  surface?: 'asphalt' | 'service' | 'gravel';
  /** Communicates how demanding the branch is to players and rival AI. */
  risk?: 'safe' | 'technical' | 'extreme';
  /** One-shot style/nitro reward for completing the branch during a lap. */
  reward?: number;
  fromTrackIndex: number;
  toTrackIndex: number;
  width: number;
  /** Intermediate control points; route endpoints come from the main track. */
  points: TrackPoint[];
}

export interface LakeDefinition {
  id: string;
  x: number;
  z: number;
  radiusX: number;
  radiusZ: number;
  rotation?: number;
  colour?: string;
}

export interface BridgeDefinition {
  trackIndex: number;
  length: number;
  width: number;
  style: 'stone' | 'steel' | 'wood';
}

export interface StuntLoopDefinition {
  id: string;
  trackIndex: number;
  radius: number;
  width: number;
  minSpeed: number;
}

export type TracksideEventKind = 'festival' | 'service' | 'market' | 'construction' | 'lookout' | 'industrial';

export type RoadSectionKind = 'launch' | 'technical' | 'hero' | 'sprint';
export type RoadStyle =
  | 'boulevard'
  | 'mountain'
  | 'village'
  | 'shore'
  | 'bridge'
  | 'expressway'
  | 'urban'
  | 'industrial'
  | 'canyon'
  | 'quarry'
  | 'desert'
  | 'jungle'
  | 'lunar';

/** Four authored acts give a lap readable geography and driving rhythm. */
export interface RoadSectionDefinition {
  id: string;
  name: string;
  kind: RoadSectionKind;
  style: RoadStyle;
  fromTrackIndex: number;
  toTrackIndex: number;
  accent?: string;
}

export type LandmarkKind =
  | 'ski-station'
  | 'lighthouse'
  | 'cable-car'
  | 'neon-tower'
  | 'quarry'
  | 'observatory'
  | 'zytglogge'
  | 'federal-palace'
  | 'chapel-bridge'
  | 'new-town-hall'
  | 'brandenburg-gate'
  | 'tv-tower'
  | 'jungle-temple'
  | 'moon-base';

/** A road-relative signature structure used for navigation at racing speed. */
export interface LandmarkDefinition {
  id: string;
  name: string;
  kind: LandmarkKind;
  trackIndex: number;
  side: 'left' | 'right';
  offset: number;
  scale?: number;
  colour?: string;
}

/** A safe, road-relative visual set piece. It never changes collision or race
 * authority; the renderer resolves its complete footprint away from asphalt. */
export interface TracksideEventDefinition {
  id: string;
  kind: TracksideEventKind;
  trackIndex: number;
  side: 'left' | 'right';
  scale?: number;
  colour?: string;
  /** Optional deterministic road gate associated with this visual compound. */
  effect?: 'nitro-zone' | 'repair-zone' | 'precision-zone';
  /** City-authored identity layered onto the normal safe dynamic obstacle. */
  setpiece?: CitySetpieceKind;
}

export interface MapFeatures {
  ramps: RampDefinition[];
  tunnels: TunnelDefinition[];
  riskGates: RiskGateDefinition[];
  mountains: number;
  alternateRoutes?: AlternateRouteDefinition[];
  lakes?: LakeDefinition[];
  bridges?: BridgeDefinition[];
  stuntLoops?: StuntLoopDefinition[];
  tracksideEvents?: TracksideEventDefinition[];
  roadSections?: RoadSectionDefinition[];
  landmarks?: LandmarkDefinition[];
}

export interface MapDefinition {
  id: string;
  name: string;
  location: string;
  tagline: string;
  biome: 'alpine' | 'coastal' | 'desert' | 'urban' | 'forest' | 'jungle' | 'lunar';
  timeOfDay: TimeOfDay;
  weather: Weather;
  laps: number;
  roadWidth: number;
  /** Optional ballistic gravity multiplier (1 = standard, the Moon ≈ 0.42). */
  gravity?: number;
  track: TrackPoint[];
  palette: MapPalette;
  scenery: MapScenery;
  features: MapFeatures;
}

export interface VisualTuning {
  paint: string;
  accent: string;
  rims: string;
  neon: string;
  neonEnabled: boolean;
  spoiler: SpoilerStyle;
  wheels: WheelStyle;
  decal: DecalStyle;
  stance: number;
}

export interface DrivetrainSpec {
  gearCount: number;
  idleRpm: number;
  redlineRpm: number;
  shiftRpm: number;
  engineBraking: number;
  shiftTime: number;
}

/** Visual-only novelty bodies; physics, audio and the cockpit follow `body`. */
export type NoveltyBody = 'hotdog' | 'duck' | 'sofa' | 'bigfoot';

export interface CarSpec {
  id: string;
  name: string;
  className: string;
  description: string;
  topSpeed: number;
  acceleration: number;
  handling: number;
  braking: number;
  offroadGrip: number;
  mass: number;
  body: 'coupe' | 'supercar' | 'muscle' | 'roadster' | 'tourer' | 'suv' | 'truck' | 'tank' | 'bike';
  /** Swaps the 3D model for a novelty build while keeping `body`'s handling. */
  novelty?: NoveltyBody;
  drivetrain: DrivetrainSpec;
  dimensions: {
    width: number;
    length: number;
    height: number;
    wheelBase: number;
  };
  defaultTuning: VisualTuning;
}

export interface RaceSettings {
  mapId: string;
  npcCount: number;
  npcDifficulty: NpcDifficulty;
  maxPlayers: number;
  /** Trust the server position after reconnects instead of the locally predicted pose. */
  antiCheat?: boolean;
  /** Resolve vehicle-to-vehicle contacts. Walls and scenery always remain solid. */
  collisions?: boolean;
  /** Add a small deterministic stream of ambient road vehicles. */
  traffic?: boolean;
  /** Room-level atmosphere override applied to the selected map. */
  timeOfDay?: TimeOfDay;
  weather?: Weather;
  /** Solo local event with medals, sectors, a personal ghost and mid-run restart. */
  timeTrial?: boolean;
  /** Explicit race format. timeTrial remains readable for pre-upgrade saves. */
  eventMode?: RaceEventMode;
  /** Optional metadata for the local, resumable multi-event NIGHT TOUR. */
  tourId?: string;
  tourStage?: number;
  /** Named AI target highlighted by race control and career rewards. */
  featuredRival?: string;
  /** Offline-only playful physics/rule modifier. Standard is career eligible. */
  arcadePreset?: ArcadePreset;
  /** Optional server-authoritative side objective selected before launch. */
  wager?: RaceWagerKind;
  /** Stable YYYY-MM-DD challenge identity; local events use it for daily records. */
  dailyChallengeId?: string;
  /** Offline-only safe rewind charges. Zero/undefined keeps normal recovery. */
  rewindLimit?: number;
  /** Per-slot rival difficulty chosen in the lobby; npcDifficulty remains the
   * dominant value for career rewards and pre-roster saves. */
  npcRoster?: NpcDifficulty[];
  /** Lobby browser listing. Undefined is private. */
  visibility?: RoomVisibility;
  /** Host lap override for lap-based formats; undefined uses the track's own. */
  laps?: number;
}

export interface RacerSnapshot {
  id: string;
  name: string;
  carId: string;
  tuning: VisualTuning;
  npc: boolean;
  connected: boolean;
  rivalStyle: RivalStyle | null;
  x: number;
  y: number;
  /** Authoritative deck height retained while the chassis is airborne. */
  groundY?: number;
  /** Baked main-road hint; -1 identifies an alternate or unknown ribbon. */
  trackSegment?: number;
  z: number;
  yaw: number;
  travelYaw: number;
  turretYaw: number;
  speed: number;
  velocityX: number;
  velocityZ: number;
  verticalSpeed: number;
  steer: number;
  drift: number;
  engineRpm: number;
  gear: number;
  wheelSlip: number;
  longitudinalAcceleration: number;
  lateralAcceleration: number;
  chassisPitch: number;
  chassisRoll: number;
  /** Spring/damper angular rates used to preserve landing and impact motion. */
  pitchVelocity?: number;
  rollVelocity?: number;
  angularVelocity?: number;
  /** Explicit authored-loop state. A null id means normal road/air physics. */
  stuntLoopId: string | null;
  stuntLoopProgress: number;
  stuntLoopSpeed: number;
  stuntLoopLane: number;
  stuntLoopLaneVelocity: number;
  nitroCharge: number;
  nitroActive: boolean;
  styleScore: number;
  styleCombo: number;
  styleMultiplier: number;
  styleAction: StyleAction;
  styleEventId: number;
  /** Authoritative cumulative count for this race; unlike styleAction it cannot
   * be overwritten by another style event between network snapshots. */
  riskLineCount: number;
  /** Continuous aerodynamic benefit earned while following another racer. */
  draftCharge: number;
  /** True during the short acceleration window after leaving a charged draft. */
  draftBoostActive: boolean;
  /** Smoothed countdown throttle used by the perfect-launch HUD. */
  launchCharge: number;
  /** Authoritative per-race counters used by district-specific mastery. */
  routeCount: number;
  perfectCornerCount: number;
  /** Heat interceptors share normal vehicle rendering and collision authority,
   * but never occupy a race position. Optional keeps old local checkpoints readable. */
  pursuit?: boolean;
  pursuitActive?: boolean;
  /** Visible pursuit escalation for Heat Run drivers. */
  heatLevel?: number;
  /** One authoritative optional objective at a time. */
  liveChallenge?: LiveChallengeKind | null;
  liveChallengeStatus?: LiveChallengeStatus;
  liveChallengeProgress?: number;
  liveChallengeTarget?: number;
  liveChallengeEventId?: number;
  liveChallengeTimeRemainingMs?: number;
  /** Three shared sectors create short duels inside every lap. */
  sectorIndex?: number;
  sectorDeltaMs?: number;
  sectorEventId?: number;
  /** Skill-earned recovery tool; it never changes rival pace invisibly. */
  comebackCharge?: number;
  comebackActive?: boolean;
  /** Named-rival state used for phase changes and radio/director cues. */
  bossPhase?: BossPhase | null;
  bossPhaseEventId?: number;
  /** ROAD RAGE authority. The marked target pays a larger takedown reward. */
  takedownCount?: number;
  roadRageScore?: number;
  roadRageTarget?: boolean;
  /** Optional pre-race wager progress. */
  wagerKind?: RaceWagerKind | null;
  wagerStatus?: RaceWagerStatus;
  wagerProgress?: number;
  wagerTarget?: number;
  /** Mastery unlock switches the car's active signature sidegrade on. */
  signatureActive?: boolean;
  /** Remaining safe rewinds in a local race. */
  rewindsRemaining?: number;
  lastProcessedInput: number;
  health: number;
  lap: number;
  checkpoint: number;
  progress: number;
  /** Number of start/finish crossings completed in this race. This remains
   * distinct from lap so the final lap is observable without displaying N+1. */
  completedLaps: number;
  place: number;
  finishedAt: number | null;
}

export interface DynamicTrackEventSnapshot {
  id: string;
  kind: DynamicTrackEventKind;
  phase: DynamicTrackEventPhase;
  /** Authoritative physical deployment. Zero is parked beyond the road edge,
   * one is fully in the blocked lane. */
  deployment: number;
  trackIndex: number;
  side: 'left' | 'right';
  remainingMs: number;
  eventId: number;
  /** Optional authored city identity; collision still uses the shared obstacle. */
  setpiece?: CitySetpieceKind;
}

export interface ProjectileSnapshot {
  id: string;
  ownerId: string;
  x: number;
  y: number;
  z: number;
  yaw: number;
}

export interface ImpactSnapshot {
  id: string;
  x: number;
  y: number;
  z: number;
  createdAt: number;
  kind: 'collision' | 'shell' | 'water';
  severity: number;
  racerIds: string[];
  responses?: ImpactResponse[];
}

export interface ImpactResponse {
  racerId: string;
  deltaVelocityX: number;
  deltaVelocityZ: number;
  deltaAngularVelocity: number;
  deltaPitchVelocity?: number;
  deltaRollVelocity?: number;
}

export interface TrafficOffsetSnapshot {
  id: string;
  offsetX: number;
  offsetZ: number;
  yawOffset: number;
}

export interface RaceSnapshot {
  type: 'snapshot';
  sequence: number;
  serverTime: number;
  /** Fixed-step traffic clock, independent from wall-clock jumps. */
  trafficTimeMs?: number;
  /** Sparse finite-mass displacement from each vehicle's authored lane. */
  trafficOffsets?: TrafficOffsetSnapshot[];
  phase: RacePhase;
  countdownMs: number;
  /** Remaining authoritative event clock, or null for untimed phases/events. */
  raceTimeRemainingMs?: number | null;
  /** At most one telegraphed lane event is live, keeping a safe route readable. */
  dynamicTrackEvent?: DynamicTrackEventSnapshot | null;
  racers: RacerSnapshot[];
  projectiles: ProjectileSnapshot[];
  impacts: ImpactSnapshot[];
}

export interface RoomMemberSummary {
  id: string;
  name: string;
  carId: string;
  connected: boolean;
  ready: boolean;
  host: boolean;
}

export interface PublicRoom {
  id: string;
  hostPlayerId: string;
  settings: RaceSettings;
  phase: RacePhase;
  /** Monotonic event run used to bank every solo rematch exactly once. */
  raceAttempt?: number;
  playerCount: number;
  createdAt: number;
  expiresAt: number;
  /**
   * Sliding inactivity lifetime. `expiresAt` moves forward with activity, so
   * clients size their local resume window from this instead of `expiresAt`.
   */
  sessionTtlMs?: number;
  /** Present on current servers; optional keeps cached offline descriptors readable. */
  members?: RoomMemberSummary[];
  spectatorCount?: number;
}

/** One row in the public lobby browser. Carries no credentials. */
export interface PublicLobbySummary {
  id: string;
  hostName: string;
  mapId: string;
  eventMode: RaceEventMode;
  phase: RacePhase;
  playerCount: number;
  maxPlayers: number;
  npcCount: number;
  timeOfDay: TimeOfDay;
  weather: Weather;
  createdAt: number;
}

export interface PlayerIdentity {
  id: string;
  token: string;
  name: string;
  carId: string;
  tuning: VisualTuning;
  /** A small handling trade-off selected before the race. */
  crewModule?: CrewModule;
  /** Local mastery unlock; effects are bounded sidegrades, not stat upgrades. */
  signatureActive?: boolean;
}
